import type {
  IEnumValue,
  IErrorApiResponse,
  IOptionItem,
  ISuccessApiResponse,
} from '@wyz/types';
import {
  OverridableQuery,
  UseQueryOptions,
  restrictApi,
  useQuery,
} from '@wyz/utils';
import { useApi, useI18n } from '~/common/context';
import { SupplierTypeCodeName } from '~/common/types';
import { ORDER_TYPE_LOOKUP } from '~/purchase/translators';

const COMMON_OPTIONS = {
  refetchOnWindowFocus: false,
  placeholderData: [],
  gcTime: Infinity,
  staleTime: Infinity,
};
type ISupplierTypeEnumValue = IEnumValue<number, SupplierTypeCodeName>;

type Options = {
  getSupplierTypes?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<ISupplierTypeEnumValue>>,
      IErrorApiResponse
    >
  > & {
    args?: { scope?: 'ALL' | 'PRICING_POLICY'; customerIds?: string[] };
  };
  getOrderTypes?: OverridableQuery<
    UseQueryOptions<Array<IOptionItem>, IErrorApiResponse>
  >;
};
export const useManageEnums = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { t } = useI18n();
  const getSupplierTypesQuery = useQuery({
    queryKey: ['supplier-types', options.getSupplierTypes?.args],
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<Array<ISupplierTypeEnumValue>>>(
        `/tenants/api/enums/supplier-types`,
        {
          method: 'POST',
          body: JSON.stringify(options.getSupplierTypes?.args),
        },
      ),
    refetchOnWindowFocus: false,
    gcTime: Infinity,
    staleTime: Infinity,
    enabled: false,
    ...options.getSupplierTypes,
  });

  const getOrderTypesQuery = useQuery({
    queryKey: ['order-types'],
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<Array<IEnumValue>>>(
        '/tenants/api/enums/order-types',
      ).then((data) =>
        data.data.map((type) => ({
          value: type.value,
          label: t(ORDER_TYPE_LOOKUP[type.value]),
        })),
      ),
    ...COMMON_OPTIONS,
    enabled: false,
    ...options.getOrderTypes,
  });

  return restrictApi(options, {
    getSupplierTypes: {
      query: getSupplierTypesQuery,
    },
    getOrderTypes: {
      query: getOrderTypesQuery,
      get(val: string) {
        return getOrderTypesQuery.data?.find((item) => item.value === val);
      },
    },
  });
};
